import {
  ChatCreateRouteAction,
  ChatRouteStartLocation,
  ChatRouteStopTarget,
} from "../../types/chat/ChatContent";
import GeoPoint from "../../types/shapes/GeoPoint";

import { isRecord } from "./chatWireGuards";
import { parseRouteCardSnapshot, parseStopTarget } from "./parseManageRouteStopsAction";
import parseSkippedRouteStops from "./parseSkippedRouteStops";

const isCoordinates = (value: unknown): value is [number, number] =>
  Array.isArray(value) && value.length === 2 && value.every((part) => typeof part === "number");

// A GeoJSON Point with a [longitude, latitude] pair, or undefined for anything else. Shared with the
// card's stored-draft reader, so the two checks cannot drift.
export const parseGeoPoint = (value: unknown): GeoPoint | undefined =>
  isRecord(value) && value.type === "Point" && isCoordinates(value.coordinates)
    ? { coordinates: value.coordinates, type: "Point" }
    : undefined;

// Undefined is malformed; null is a real value — the turn carried no position — and is kept.
const parseStartLocation = (value: unknown): ChatRouteStartLocation | null | undefined => {
  if (value === null) {
    return null;
  }
  if (!isRecord(value) || typeof value.address !== "string") {
    return undefined;
  }
  const geoPoint = parseGeoPoint(value.geoPoint);
  return geoPoint ? { address: value.address, geoPoint } : undefined;
};

// Follows the platform's own guard (create-route/parse-action.ts): at least one stop, every stop a
// Company or Person, and a string start time. The start location is null or a full address + Point,
// which is what the platform's type promises — its guard only checks that geoPoint is an object.
// A malformed route snapshot or skipped entry is dropped on its own, as for manage_route_stops.
const parseCreateRouteAction = (
  raw: Record<string, unknown>,
  warnings: string[]
): ChatCreateRouteAction | undefined => {
  if (
    typeof raw.routeName !== "string" ||
    !Array.isArray(raw.stops) ||
    raw.stops.length === 0 ||
    !isRecord(raw.proposedValues) ||
    typeof raw.proposedValues.startAt !== "string"
  ) {
    return undefined;
  }
  // Any unreadable stop rejects the whole proposal, so the lengths must still match after the filter.
  const stops = raw.stops
    .map(parseStopTarget)
    .filter((stop): stop is ChatRouteStopTarget => !!stop);
  const startLocation = parseStartLocation(raw.proposedValues.startLocation);
  if (stops.length !== raw.stops.length || startLocation === undefined) {
    return undefined;
  }
  const routeCard = parseRouteCardSnapshot(raw.routeCard);
  return {
    proposedValues: { startAt: raw.proposedValues.startAt, startLocation },
    ...(routeCard ? { routeCard } : {}),
    routeName: raw.routeName,
    ...parseSkippedRouteStops(raw.skippedStops, warnings),
    stops,
    type: "create_route",
  };
};

export default parseCreateRouteAction;
